/**
 * Unica sezione note della chat, con due tipi ben separati:
 * - "Nota della chat": resta legata a questa conversazione (whatsapp chat notes);
 * - "Nota sulla scheda": finisce nella scheda del Lead/Cliente (lead_notes),
 *   la stessa che si vede aprendo la scheda.
 * Nessuna delle due arriva al cliente.
 */
import { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { StickyNote, FileText, Send, Trash2, Loader2 } from 'lucide-react';
import { useWhatsAppChatMetaStore } from '@/lib/stores/whatsappChatMetaStore';
import { useLeadNoteStore } from '@/lib/stores/leadNoteStore';
import { useApp } from '@/components/AppProvider';
import { iconTip } from '@/components/messages/ChatTooltip';

type NoteKind = 'chat' | 'record';

interface Props {
  chatJid: string;
  organizationId: string;
  /** Lead/Cliente collegato alla chat: senza, c'e' solo la nota della chat. */
  clientId: string | null;
  clientName?: string | null;
}

const fmt = (iso: string) =>
  new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

export function ChatNotesSection({ chatJid, organizationId, clientId, clientName }: Props) {
  const { toast, confirm } = useApp();
  const [kind, setKind] = useState<NoteKind>('chat');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const chatNotes = useWhatsAppChatMetaStore((s) => s.chatNotes.get(chatJid)) || [];
  const chatNotesLoading = useWhatsAppChatMetaStore((s) => s.notesLoading);
  const fetchChatNotes = useWhatsAppChatMetaStore((s) => s.fetchNotes);
  const addChatNote = useWhatsAppChatMetaStore((s) => s.addNote);
  const deleteChatNote = useWhatsAppChatMetaStore((s) => s.deleteNote);

  const recordNotes = useLeadNoteStore((s) => s.notes);
  const recordLoading = useLeadNoteStore((s) => s.loading);
  const fetchRecordNotes = useLeadNoteStore((s) => s.fetchNotesByLeadId);
  const createRecordNote = useLeadNoteStore((s) => s.createNote);
  const deleteRecordNote = useLeadNoteStore((s) => s.deleteNote);

  useEffect(() => { fetchChatNotes(chatJid, organizationId); }, [chatJid, organizationId, fetchChatNotes]);
  useEffect(() => { if (clientId) fetchRecordNotes(clientId); }, [clientId, fetchRecordNotes]);
  useEffect(() => { if (!clientId && kind === 'record') setKind('chat'); }, [clientId, kind]);

  const who = clientName || 'Lead/Cliente';

  const handleSend = async () => {
    const value = text.trim();
    if (!value || sending) return;
    setSending(true);
    try {
      if (kind === 'chat') {
        await addChatNote(chatJid, value, organizationId);
      } else if (clientId) {
        const ok = await createRecordNote(clientId, value);
        if (!ok) throw new Error('nota non salvata');
        toast.success(`Nota aggiunta alla scheda di ${who}`);
      }
      setText('');
    } catch {
      toast.error('Non sono riuscito a salvare la nota');
    } finally {
      setSending(false);
    }
  };

  const askDelete = async (onYes: () => Promise<unknown> | void) => {
    const ok = await confirm({ title: 'Eliminare la nota?', message: 'La nota verrà cancellata per tutto il team.', confirmText: 'Elimina', cancelText: 'Annulla', type: 'danger' });
    if (ok) await onYes();
  };

  const tabCls = (active: boolean) =>
    `flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-[12px] font-medium transition-colors ${
      active ? 'bg-white dark:bg-[#2f2f2f] text-[#37352F] dark:text-white shadow-sm' : 'text-[#5F5E5B] dark:text-white/70 hover:text-[#37352F] dark:hover:text-white'
    }`;

  const loading = kind === 'chat' ? chatNotesLoading : recordLoading;
  const empty = kind === 'chat' ? chatNotes.length === 0 : recordNotes.length === 0;

  return (
    <div className="flex flex-col min-h-0 h-full">
      <div role="tablist" aria-label="Tipo di nota" className="flex gap-0.5 p-0.5 rounded-md bg-[rgba(55,53,47,0.06)] dark:bg-white/10">
        <button role="tab" aria-selected={kind === 'chat'} onClick={() => setKind('chat')} className={tabCls(kind === 'chat')}>
          <StickyNote className="w-3.5 h-3.5" /> Nota della chat
          {chatNotes.length > 0 && <span className="tabular-nums">({chatNotes.length})</span>}
        </button>
        <button role="tab" aria-selected={kind === 'record'} onClick={() => clientId && setKind('record')} disabled={!clientId}
          className={`${tabCls(kind === 'record')} disabled:opacity-50 disabled:cursor-not-allowed`}>
          <FileText className="w-3.5 h-3.5" /> Nota sulla scheda
          {clientId && recordNotes.length > 0 && <span className="tabular-nums">({recordNotes.length})</span>}
        </button>
      </div>

      <p className="text-[11px] leading-snug text-[#5F5E5B] dark:text-white/70 mt-2 mb-2">
        {kind === 'chat'
          ? 'Resta solo in questa conversazione. La vede il tuo team, mai il cliente.'
          : `Finisce nella scheda di ${who}, insieme alle altre note. La vede il tuo team, mai il cliente.`}
        {!clientId && ' Per scrivere sulla scheda, collega prima la chat a un Lead o Cliente.'}
      </p>

      <div className="flex-1 min-h-0 overflow-y-auto space-y-1.5 mb-2">
        {loading && <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-[#5F5E5B]" /></div>}
        {!loading && empty && <p className="text-[12px] text-[#5F5E5B] dark:text-white/60 text-center py-3">Nessuna nota</p>}
        {kind === 'chat' && chatNotes.map((note) => (
          <div key={note.id} className="group relative bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-800/30 rounded px-2.5 py-2">
            <div className="flex items-start justify-between gap-1 mb-0.5 pr-5">
              <span className="text-[11px] font-semibold text-amber-800 dark:text-amber-300">{note.author_name}</span>
              <span className="text-[11px] text-[#5F5E5B] dark:text-white/60">{fmt(note.created_at)}</span>
            </div>
            <p className="text-[12px] text-[#37352F] dark:text-white whitespace-pre-wrap">{note.content}</p>
            <button onClick={() => askDelete(() => deleteChatNote(note.id, chatJid))}
              className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 focus:opacity-100 p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20"
              {...iconTip('Elimina nota')}>
              <Trash2 className="w-3 h-3 text-red-500" />
            </button>
          </div>
        ))}
        {kind === 'record' && recordNotes.map((note) => (
          <div key={note.id} className="group relative bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border border-[rgba(55,53,47,0.09)] dark:border-white/10 rounded px-2.5 py-2">
            <div className="flex items-start justify-between gap-1 mb-0.5 pr-5">
              <span className="text-[11px] font-semibold text-[#37352F] dark:text-white">{note.author?.full_name || note.author?.email || 'Utente'}</span>
              <span className="text-[11px] text-[#5F5E5B] dark:text-white/60">{fmt(note.created_at)}</span>
            </div>
            <div className="text-[12px] text-[#37352F] dark:text-white/90 line-clamp-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note.content || '') }} />
            <button onClick={() => askDelete(() => deleteRecordNote(note.id))}
              className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 focus:opacity-100 p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20"
              {...iconTip('Elimina nota')}>
              <Trash2 className="w-3 h-3 text-red-500" />
            </button>
          </div>
        ))}
      </div>

      <div className="flex gap-1.5 flex-shrink-0">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={kind === 'chat' ? 'Scrivi una nota su questa chat...' : `Scrivi una nota sulla scheda di ${who}...`}
          aria-label={kind === 'chat' ? 'Nuova nota della chat' : 'Nuova nota sulla scheda'}
          rows={2}
          className="flex-1 text-[12px] resize-none bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border border-[rgba(55,53,47,0.16)] dark:border-white/10 rounded px-2 py-1.5 outline-none focus:border-[#2383E2] text-[#37352F] dark:text-white placeholder:text-[#6F6E69]"
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
        />
        <button onClick={handleSend} disabled={!text.trim() || sending}
          className="self-end p-1.5 rounded bg-[#2383E2] hover:bg-[#1a6fc2] disabled:opacity-50 text-white"
          {...iconTip('Salva nota')}>
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}
